import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Sparkles, ArrowRight, BarChart3, TrendingUp, Zap, Shield } from 'lucide-react';

export default function LoginPage() {
  const { login } = useAuth();
  const location = useLocation();
  const error = location.state?.error;

  return (
    <div className="min-h-screen flex flex-col lg:flex-row" style={{ backgroundColor: '#FFFFFF' }}>
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-[56%] relative overflow-hidden">
        {/* Background effects */}
        <div className="absolute -top-32 -left-32 w-[600px] h-[600px] rounded-full blur-[160px]" style={{ backgroundColor: 'rgba(50,180,172,0.06)' }} />
        <div className="absolute -bottom-24 right-0 w-[450px] h-[450px] rounded-full blur-[140px]" style={{ backgroundColor: 'rgba(50,180,172,0.03)' }} />

        {/* Grid overlay */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.04) 1px, transparent 1px)',
          backgroundSize: '80px 80px'
        }} />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-14 w-full">
          {/* Logo */}
          <div>
            <div className="flex items-center gap-3.5">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-xl" style={{ background: 'linear-gradient(135deg, #32B4AC, #2a9a93)', boxShadow: '0 8px 32px rgba(50,180,172,0.25)' }}>
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold tracking-tight font-heading" style={{ color: '#1A1A1A' }}>AURA</h1>
                <p className="text-xs tracking-wide" style={{ color: '#6B7280' }}>AI-Unified Review Analytics</p>
              </div>
            </div>
          </div>

          {/* Hero */}
          <div className="max-w-lg space-y-8">
            <div>
              <h2 className="text-5xl xl:text-6xl font-extrabold leading-[1.1] tracking-tight font-heading" style={{ color: '#1A1A1A' }}>
                Analytics that
              </h2>
              <h2 className="text-5xl xl:text-6xl font-highlight italic leading-[1.2]" style={{ color: '#32B4AC' }}>
                drive decisions
              </h2>
            </div>

            <p className="text-base leading-relaxed max-w-md" style={{ color: '#6B7280' }}>
              Turn customer reviews into actionable insights. Understand sentiment, spot trends, and make data-driven decisions across all your marketplaces.
            </p>

            {/* Features */}
            <div className="space-y-3.5">
              {[
                { icon: BarChart3, text: 'Unified analytics across Amazon, Flipkart, Blinkit & more' },
                { icon: Zap, text: 'AI-powered sentiment analysis & topic detection' },
                { icon: TrendingUp, text: 'Real-time insights for faster decision making' },
              ].map((feat, i) => (
                <div key={i} className="flex items-center gap-3.5">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#F3F4F6', border: '1px solid rgba(50,180,172,0.15)' }}>
                    <feat.icon className="w-4 h-4" style={{ color: '#32B4AC' }} />
                  </div>
                  <span className="text-sm" style={{ color: '#6B7280' }}>{feat.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Dashboard preview */}
          <div className="relative">
            <div className="absolute inset-x-0 bottom-0 h-16 z-10" style={{ background: 'linear-gradient(to top, #FFFFFF, transparent)' }} />
            <div className="rounded-2xl p-4 shadow-2xl" style={{ backgroundColor: '#F9FAFB', border: '1px solid rgba(0,0,0,0.06)' }}>
              {/* Stats */}
              <div className="flex gap-3 mb-3">
                {[
                  { label: 'Analysed Reviews', value: '24,531' },
                  { label: 'Positive', value: '78%' },
                  { label: 'Avg Rating', value: '4.2' },
                ].map((stat, i) => (
                  <div key={i} className="flex-1 rounded-xl p-3" style={{ backgroundColor: 'rgba(50,180,172,0.06)', border: '1px solid rgba(50,180,172,0.1)' }}>
                    <div className="text-[10px] mb-1" style={{ color: '#9CA3AF' }}>{stat.label}</div>
                    <div className="text-xl font-bold font-heading" style={{ color: '#32B4AC' }}>{stat.value}</div>
                  </div>
                ))}
              </div>
              {/* Chart */}
              <div className="h-24 rounded-xl flex items-end p-3 gap-1.5" style={{ backgroundColor: 'rgba(0,0,0,0.02)', border: '1px solid rgba(0,0,0,0.04)' }}>
                {[35, 55, 40, 72, 48, 62, 85, 52, 68, 78, 44, 90].map((h, i) => (
                  <div
                    key={i}
                    className="flex-1 rounded-t-sm"
                    style={{
                      height: `${h}%`,
                      backgroundColor: '#32B4AC',
                      opacity: 0.3 + (i * 0.05),
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Login */}
      <div className="w-full lg:w-[44%] flex flex-col" style={{ backgroundColor: '#FFFFFF', borderLeft: '1px solid rgba(0,0,0,0.06)' }}>
        {/* Mobile header */}
        <header className="lg:hidden p-6 pb-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #32B4AC, #2a9a93)' }}>
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="text-2xl font-bold font-heading" style={{ color: '#1A1A1A' }}>AURA</span>
          </div>
        </header>

        {/* Login form */}
        <div className="flex-1 flex items-center justify-center px-8 py-12">
          <div className="w-full max-w-[380px]">
            {/* Title */}
            <div className="mb-8">
              <h1 className="text-2xl font-bold mb-2 font-heading tracking-tight" style={{ color: '#1A1A1A' }}>
                Log in to your account
              </h1>
              <p className="text-sm" style={{ color: '#9CA3AF' }}>
                Welcome back! Please sign in to continue.
              </p>
            </div>

            {/* Error */}
            {error && (
              <div className="mb-6 p-3.5 rounded-xl text-sm" style={{ backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.15)', color: '#ef4444' }}>
                {error}
              </div>
            )}

            {/* Google button */}
            <Button
              onClick={login}
              className="w-full h-12 font-medium rounded-xl flex items-center justify-center gap-3 transition-all duration-200 hover:shadow-lg cursor-pointer"
              style={{ backgroundColor: '#F3F4F6', color: '#1A1A1A', border: '1px solid rgba(0,0,0,0.08)' }}
              data-testid="google-signin-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Continue with Google
            </Button>

            {/* Divider */}
            <div className="flex items-center gap-4 my-6">
              <div className="flex-1 h-px" style={{ backgroundColor: 'rgba(0,0,0,0.06)' }} />
              <span className="text-[10px] uppercase tracking-[0.2em] font-medium" style={{ color: '#9CA3AF' }}>Access</span>
              <div className="flex-1 h-px" style={{ backgroundColor: 'rgba(0,0,0,0.06)' }} />
            </div>

            {/* Domain notice */}
            <div className="p-4 rounded-xl mb-6" style={{ backgroundColor: '#F9FAFB', border: '1px solid rgba(0,0,0,0.06)' }}>
              <div className="flex items-center gap-3">
                <Shield className="w-4 h-4 flex-shrink-0" style={{ color: '#9CA3AF' }} />
                <p className="text-sm" style={{ color: '#6B7280' }}>
                  Restricted to{' '}
                  <span className="font-semibold" style={{ color: '#32B4AC' }}>@wellversed.in</span>
                  {' '}domain only
                </p>
              </div>
            </div>

            {/* CTA */}
            <div className="p-4 rounded-xl" style={{ backgroundColor: 'rgba(50,180,172,0.04)', border: '1px solid rgba(50,180,172,0.12)' }}>
              <p className="text-xs mb-1.5" style={{ color: '#9CA3AF' }}>New to AURA?</p>
              <div className="flex items-center justify-between text-sm font-medium group cursor-pointer" style={{ color: '#32B4AC' }}>
                <span className="group-hover:underline underline-offset-2">Learn how AURA can transform your review analytics</span>
                <ArrowRight className="w-4 h-4 flex-shrink-0 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="p-6 text-center">
          <p className="text-xs" style={{ color: '#D1D5DB' }}>
            &copy; 2026 AURA by Wellversed. All rights reserved.
          </p>
        </footer>
      </div>
    </div>
  );
}
